import { useEffect, useMemo, useRef } from 'react';
import type { Marker as MarkerType } from 'leaflet';
import { divIcon } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Text } from '@querodelivery/uni-react';
import { FOCUSED_PIN_Z_INDEX } from './constants';
import type { MapPosition } from '../types';
import styles from './route-endpoint-pin.module.scss';

const ENDPOINT_ICONS = {
  start:
    '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true"><path d="M9.6 16.7 5.2 12.3l1.4-1.4 3 3 7.8-7.8 1.4 1.4Z"/></svg>',
  end: '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true"><path d="M6 3h1.5v18H6Zm2.2 1.2 11 4.6-11 4.6V4.2Z"/></svg>',
};

interface RouteEndpointPinParams {
  kind: 'start' | 'end';
  position: MapPosition;
  label: string;
  isFocused: boolean;
  onClick?: () => void;
}

export const RouteEndpointPin = ({
  kind,
  position,
  label,
  isFocused,
  onClick,
}: RouteEndpointPinParams) => {
  const markerRef = useRef<MarkerType>(null);
  const icon = useMemo(
    () =>
      divIcon({
        className: styles.wrapper,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
        popupAnchor: [0, -12],
        html: `<div class="${styles.pin} ${styles[kind]}">${ENDPOINT_ICONS[kind]}</div>`,
      }),
    [kind],
  );

  useEffect(() => {
    if (!isFocused) {
      markerRef.current?.closePopup();
      return;
    }

    markerRef.current?.openPopup();
  }, [isFocused]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      zIndexOffset={isFocused ? FOCUSED_PIN_Z_INDEX : 0}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
        mouseout: () => {
          if (!isFocused) {
            markerRef.current?.closePopup();
          }
        },
        click: () => onClick?.(),
      }}
    >
      <Popup closeButton={false} autoPan={false}>
        <div className={styles.content}>
          <Text fontSize="xs" fontWeight="semibold" tone="highlight">
            {label}
          </Text>
        </div>
      </Popup>
    </Marker>
  );
};
